import React, { useContext, useEffect, useMemo, useState } from 'react';
import { type CountrySettings as CountrySettingsType, GlobalContext } from '../../context/GlobalContext';
import { type UserData as UserDataType } from '../../hooks/auth';
import Stepper from '../../components/Stepper';
import { ContainerSpinner } from '../../components/Spinner';
import Input from '../../components/Input';
import Notice from '../../components/Notice';
import { Button } from '../../shadcn/Button';
import Select from '../../components/Select';
import PhoneInput from '../../components/PhoneInput';
import { AlertTriangle, ArrowLeft, CircleCheck, Clock, Info, Phone, Search, TicketPercent } from 'lucide-react';
import { format_currency, ucfirst, format_identification_with_prefix } from '../../utils/formatters';
import { validate_phone_number } from '../../utils/validate';
import { callAPI } from '../../hooks/api';
import { CARRIERS_BY_OPERATOR } from '../../utils/constants';
import firestore from '../../firestore';
import { get_customer_financing_overrides, useBCVExchangeRate } from '../../hooks/firebase';
import NewSale from '../nuevo-registro';

type RecargaStep = 1 | 2 | 3 | 4 | 5 | 6;

export default function Recargar() {
	const { userData, CountrySettings, LevelsConfig } = useContext(GlobalContext);
	const uidPrefix = CountrySettings?.uid_prefix || '';
	const seller_store = userData?.master_store?.email;
	const store_user = userData?.email;
	const sub_stores = userData?.master_store?.sub_stores || [];

	const country = userData?.country || '';
	const currencyCode = CountrySettings?.currency_code || '$';
	const bcvRate = useBCVExchangeRate(country);

	const [page, setPage] = useState<RecargaStep>(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [showRegistration, setShowRegistration] = useState(false);

	// Availability check (store & balance)
	const { checkingAvailability, serviceAvailable, availabilityMessage } = useRecargaAvailability(
		CountrySettings as CountrySettingsType,
		userData as UserDataType,
		country
	);

	// Step 1: select operator and phone number
	const [phoneNumber, setPhoneNumber] = useState('');
	const [operator, setOperator] = useState<string>('');

	// Step 2: optional user lookup (for financing)
	const [userIdentificationNumber, setUserIdentificationNumber] = useState('');
	const [userEmail, setUserEmail] = useState('');
	const [userLevel, setUserLevel] = useState<'level_1' | 'level_2' | 'level_3' | 'level_4' | null>(null);
	// The customer's own recarga fee (Users.financing_overrides), as the backend charges it
	const [recargaFeeOverride, setRecargaFeeOverride] = useState<number | undefined>(undefined);
	const [recargaFeeOverrideExpiresAt, setRecargaFeeOverrideExpiresAt] = useState<number | null>(null);
	const [canUserFinance, setCanUserFinance] = useState(false);
	const [isFirstRecharge, setIsFirstRecharge] = useState(false);

	// Step 4: amount
	const [amount, setAmount] = useState<number | null>(null);

	// Calculate minimum amount for current operator
	const minAmount = useMemo(() => {
		const options = getAmountOptions(CountrySettings!, country, operator, isFirstRecharge, bcvRate);
		return options.length > 0 ? Math.min(...options) : null;
	}, [CountrySettings, country, operator, isFirstRecharge, bcvRate]);

	// Check if first recharge has a reduced max limit
	const hasReducedFirstRechargeLimit = useMemo(() => {
		if (!isFirstRecharge || !operator) return false;

		const operatorConfig = (CountrySettings?.services_config?.recargas?.operators)?.[operator as "viva"];
		if (!operatorConfig || operatorConfig.new_user_max === undefined) return false;

		let newUserMax = operatorConfig.new_user_max;
		const regularMax = operatorConfig.max;

		// For Venezuela, convert new_user_max from dollars to Bs using BCV rate
		if (country === 'venezuela') {
			newUserMax = newUserMax * bcvRate;
		}

		return newUserMax < regularMax;
	}, [isFirstRecharge, CountrySettings, operator, country, bcvRate]);

	// Auto-select minimum amount when operator changes or when no amount is selected
	useEffect(() => {
		if (minAmount !== null) {
			const availableOptions = getAmountOptions(CountrySettings!, country, operator, isFirstRecharge, bcvRate);
			const isCurrentAmountValid = amount !== null && availableOptions.includes(amount);

			// Set to minimum if no amount selected or current amount is not valid for new operator
			if (amount === null || !isCurrentAmountValid) {
				setAmount(minAmount);
			}
		}
	}, [minAmount, amount, CountrySettings, country, operator, isFirstRecharge, bcvRate]);

	// Step 5: choose financed or not and confirm
	const [isFinanced, setIsFinanced] = useState(false);
	const [checkboxChecked, setCheckboxChecked] = useState(false);

	// Reprice only before a contract exists, including when returning from the contract step.
	useEffect(() => {
		if (recargaFeeOverrideExpiresAt === null || page > 4) return;
		let timeout: ReturnType<typeof setTimeout>;
		const checkExpiry = () => {
			const remaining = recargaFeeOverrideExpiresAt - Date.now();
			if (remaining <= 0) {
				setRecargaFeeOverride(undefined);
				setRecargaFeeOverrideExpiresAt(null);
				setCheckboxChecked(false);
				return;
			}
			timeout = setTimeout(checkExpiry, Math.min(remaining, 2 ** 31 - 1));
		};
		checkExpiry();
		return () => clearTimeout(timeout);
	}, [recargaFeeOverrideExpiresAt, page]);

	// Reset payment confirmation when financing option changes or leaving page 4
	useEffect(() => {
		if (page !== 4) {
			setCheckboxChecked(false);
		}
	}, [page, isFinanced]);

	const steps = useMemo(() => {
		const baseSteps = [
			{ label: 'Operador', pages: [1] },
			{ label: country === 'peru' ? 'DNI' : 'Cédula', pages: [2] },
			{ label: 'Monto', pages: [3] },
			{ label: 'Confirmar', pages: [4] }
		];

		// Add contract step only if financed
		if (page >= 4 && isFinanced) {
			baseSteps.push({ label: 'Contrato', pages: [5] });
		}

		// Success step
		baseSteps.push({ label: 'Éxito', pages: [6] });

		return baseSteps;
	}, [country, page, isFinanced]);

	// Step 5: accept contract if financed
	const [subStore, setSubStore] = useState('');
	const [contractAccepted, setContractAccepted] = useState(false);
	useEffect(() => {
		let unsubscribe: any;

		if (page === 5 && isFinanced) {
			unsubscribe = firestore
				.collection('Anexos')
				.where('identification_number', '==', format_identification_with_prefix(userIdentificationNumber, uidPrefix))
				.where('terms_accepted', '==', true)
				.where('related_device', '==', null)
				.onSnapshot((snapshot) => {
					if (!snapshot.empty) {
						setContractAccepted(true);
					}
				});
		}

		return () => {
			if (unsubscribe) {
				unsubscribe?.();
			}
		};

		//eslint-disable-next-line
	}, [page]);

	// Auto-finalize recharge when contract is accepted
	useEffect(() => {
		if (page === 5 && contractAccepted && !loading && !error) {
			handleFinalizeRecharge();
		}
		//eslint-disable-next-line
	}, [page, contractAccepted]);

	// Derived config
	const recargaFee = useMemo(() => {
		try {
			if (recargaFeeOverride !== undefined) return recargaFeeOverride;
			if (!LevelsConfig || !userLevel) return 0;
			return Number((LevelsConfig[country as 'venezuela' | 'peru' | 'rd'] as any)?.[userLevel]?.recarga_fee || 0);
		} catch {
			return 0;
		}
	}, [recargaFeeOverride, LevelsConfig, country, userLevel]);

	// Effective fee is only applied when financed
	const effectiveFee = useMemo(() => {
		if (isFinanced && country === 'venezuela' && recargaFeeOverride !== undefined) {
			// The backend rounds IVA separately for a customer's own fee.
			return Math.round((recargaFee + Math.round(recargaFee * 16) / 100) * 100) / 100;
		}
		return isFinanced ? recargaFee * (country === 'venezuela' ? 1.16 : 1) : 0;
	}, [isFinanced, recargaFee, recargaFeeOverride, country]);
	const financingPrincipal = country === 'venezuela' ? Number(((amount || 0) / bcvRate).toFixed(2)) : amount || 0;
	const financedTotal =
		recargaFeeOverride !== undefined
			? Math.round((financingPrincipal + effectiveFee) * 100) / 100
			: (country === 'venezuela' ? (amount || 0) / bcvRate : amount || 0) + effectiveFee;

	// Handle recharge registration
	const handleFinalizeRecharge = async () => {
		try {
			setLoading(true);
			setError('');

			const [response, error] = await callAPI(
				'https://api.krece.app/registration/storerecargaregistration/',
				'POST',
				{
					email: userEmail,
					phone_number: phoneNumber,
					amount: amount || 0,
					operator: operator,
					country: country,
					sub_store: subStore ? subStore : (userData?.store_name || ''),
					financed: isFinanced
				},
				userData
			);

			if (!error && response?.success) {
				setPage(6);
			} else {
				setError(error || response?.status || 'Error al procesar la recarga');
			}
		} catch (err) {
			setError('Error al procesar la recarga. Intente nuevamente.');
		} finally {
			setLoading(false);
		}
	};

	if (!userData || !CountrySettings || !LevelsConfig || checkingAvailability) return <ContainerSpinner size={3} />;

	// Show registration component if requested
	if (showRegistration) {
		return <NewSale store_recharge_coupon={true} />;
	}

	if (!serviceAvailable) {
		return (
			<div className="m-auto flex min-h-full min-w-full flex-col items-center justify-center p-4 md:p-6">
				<div className="w-[500px] max-w-full rounded-xl border-[1.5px] border-neutral-900 bg-neutral-1000/25 p-6 text-center light:bg-white md:bg-background/50 md:p-8">
					<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-950/30 light:bg-red-50">
						<AlertTriangle className="h-6 w-6 text-red-400" />
					</div>
					<h1 className="mb-1 text-2xl font-semibold tracking-tighter text-neutral-0">Servicio no disponible</h1>
					<p className="text-neutral-300">{availabilityMessage || 'Por favor, intenta más tarde.'}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="m-auto flex min-h-full min-w-full flex-col items-center justify-start">
			<Stepper currentPage={page} steps={steps} />
			<div className="m-auto flex h-full w-full flex-col items-center justify-center p-4 md:p-6">
				<div className="w-[500px] max-w-full rounded-xl border-[1.5px] border-neutral-900 bg-neutral-1000/25 p-6 shadow-lg duration-300 animate-in fade-in slide-in-from-bottom-5 light:bg-white light:shadow-none md:bg-background/50 md:p-8">
					{page === 1 &&
						render_header_and_content(
							'Recargar saldo',
							'Selecciona el operador e ingresa el número a recargar',
							<Phone />,
							<div className="space-y-4">
								<Select
									floatingLabel="Operador"
									options={
										CountrySettings?.services_config?.recargas?.operators
											? Object.keys(CountrySettings.services_config.recargas.operators).map((op) => ({
													label: ucfirst(op),
													value: op
												}))
											: []
									}
									defaultValue={operator}
									placeholder="Selecciona un operador"
									onSelectedChange={(val) => {
										setOperator(typeof val === 'string' ? val : '');
										setPhoneNumber('');
									}}
								/>
								<PhoneInput
									key={operator || 'no-operator'}
									disabled={!operator}
									country={country}
									value={phoneNumber}
									onChange={(val) => setPhoneNumber(val)}
									operator={operator as keyof typeof CARRIERS_BY_OPERATOR}
								/>
								<Button
									disabled={!operator || !validate_phone_number(phoneNumber, country)}
									onClick={() => setPage(2)}
									variant="primary"
								>
									Continuar
								</Button>
							</div>
						)}

					{page === 2 &&
						render_header_and_content(
							'Identificación del cliente',
							null,
							<Search />,
							<div className="space-y-4">
								<Input
									name="cedula"
									floatingLabel="Identificación del cliente (opcional)"
									value={userIdentificationNumber}
									onChange={setUserIdentificationNumber}
									placeholder="Ingrese la cédula, DNI, etc"
									allowedChars="numbers_only"
									validation={(value) => !value || value.trim().length >= 5}
								/>
								<p className="text-xs text-neutral-400">
									Este paso es opcional. Solo es necesario si el cliente desea financiar su recarga.
								</p>
								{error && <Notice message={error} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={loading || !userIdentificationNumber || userIdentificationNumber.trim().length < 5}
										loading={loading}
										onClick={async () => {
											setError('');

											// If identification is entered, validate it
											if (userIdentificationNumber.trim().length < 5) {
												setError('Por favor ingrese una identificación válida (mínimo 5 caracteres).');
												return;
											}

											setLoading(true);
											try {
												const [response, error] = await callAPI(
													'https://api.krece.app/registration/recargaavailable/',
													'GET',
													{ identification_number: userIdentificationNumber },
													userData,
													country
												);

												if (error || !response || response?.new_user || !response?.email) {
													setError(
														'El cliente no está registrado en Krece. Para financiar, el cliente debe estar registrado. Puede omitir este paso si el pago será inmediato.'
													);
													return;
												}

												setUserEmail(response?.email || '');
												setUserLevel(response?.level || 'level_1');
												const customerOverrides = await get_customer_financing_overrides(
													response.email,
													userData,
													country
												);
												setRecargaFeeOverride(customerOverrides?.active.recarga?.fee);
												setRecargaFeeOverrideExpiresAt(
													customerOverrides?.active.recarga ? customerOverrides.expires_at_ms : null
												);
												setCanUserFinance(response?.recarga_financed_access === true);
												setIsFirstRecharge(response?.first_recharge === true && response?.other_purchases === false);
												setPage(3);
											} catch {
												setError('Ocurrió un problema, por favor intente nuevamente o contacte a soporte.');
											} finally {
												setLoading(false);
											}
										}}
										variant="primary"
									>
										Continuar
									</Button>
									<Button
										Icon={<ArrowLeft className="size-5" />}
										type="button"
										onClick={() => {
											setPage(1);
											setUserIdentificationNumber('');
											setUserEmail('');
											setUserLevel(null);
											setRecargaFeeOverride(undefined);
											setRecargaFeeOverrideExpiresAt(null);
											setCanUserFinance(false);
											setIsFirstRecharge(false);
											setError('');
										}}
										variant="secondary"
									>
										Atrás
									</Button>
								</div>
								<Button
									type="button"
									disabled={loading}
									onClick={() => {
										// Clear all user data and skip to next page
										setUserIdentificationNumber('');
										setUserEmail('');
										setUserLevel(null);
										setRecargaFeeOverride(undefined);
										setRecargaFeeOverrideExpiresAt(null);
										setCanUserFinance(false);
										setIsFirstRecharge(false);
										setError('');
										setPage(3);
									}}
									variant="ghost"
									className="w-full"
								>
									Omitir este paso
								</Button>
							</div>
						)}

					{page === 3 &&
						render_header_and_content(
							'Seleccionar monto',
							'Elige cuánto saldo quieres recargar',
							<Info />,
							<div className="space-y-4">
								<Select
									floatingLabel="Monto de recarga"
									options={getAmountOptions(CountrySettings, country, operator, isFirstRecharge, bcvRate).map((val) => ({
										label: `${country === 'venezuela' ? 'Bs. ' : currencyCode} ${format_currency(val, true)}`,
										value: String(val)
									}))}
									defaultValue={amount ? String(amount) : minAmount ? String(minAmount) : undefined}
									onSelectedChange={(val) => {
										const parsed = typeof val === 'string' ? parseFloat(val) : null;
										setAmount(parsed && !isNaN(parsed) ? parsed : null);
										setError('');
									}}
								/>
								{hasReducedFirstRechargeLimit && (
									<div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
										<div className="flex items-start space-x-2">
											<div className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500/20">
												<Info className="h-2.5 w-2.5 text-amber-400" />
											</div>
											<div className="flex-1">
												<p className="text-xs font-medium text-amber-300 light:text-amber-900">Primera recarga</p>
												<p className="text-xs text-amber-200/80 light:text-amber-900/80">
													Para futuras recargas, este cliente tendrá acceso a recargas de montos mayores.
												</p>
											</div>
										</div>
									</div>
								)}
								{error && <Notice message={error} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={!amount}
										onClick={() => {
											if (!amount) return;
											setPage(4);
										}}
										variant="primary"
									>
										Continuar
									</Button>
									<Button Icon={<ArrowLeft className="size-5" />} type="button" onClick={() => setPage(2)} variant="secondary">
										Atrás
									</Button>
								</div>
							</div>
						)}

					{page === 4 &&
						render_header_and_content(
							'Confirmar recarga',
							'Revisa los detalles antes de confirmar',
							<Info />,
							<div className="space-y-4">
								<div className="space-y-4">
									<h3 className="text-sm font-semibold tracking-tight text-neutral-100">Opción de pago</h3>
									<div className="grid gap-3 duration-300 animate-in fade-in slide-in-from-bottom-2">
										{/* Immediate Payment Option */}
										<div
											onClick={() => setIsFinanced(false)}
											className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] ${
												!isFinanced
													? 'border-primary-500 bg-primary-500/5 shadow-md shadow-primary-500/20 ring-1 ring-primary-500/20'
													: 'border-neutral-700 bg-neutral-900/50 hover:border-neutral-600 hover:bg-neutral-900/70'
											}`}
										>
											<div className="flex items-start space-x-3">
												<div
													className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200 ${
														!isFinanced
															? 'border-primary-500 bg-primary-500 shadow-sm shadow-primary-500/30'
															: 'border-neutral-600 bg-neutral-900'
													}`}
												>
													{!isFinanced && (
														<div className="h-2 w-2 rounded-full bg-white duration-200 animate-in zoom-in-50"></div>
													)}
												</div>
												<div className="min-w-0 flex-1">
													<div className="flex items-center justify-between">
														<h4
															className={`text-sm font-medium transition-colors ${
																!isFinanced ? 'text-primary-400 light:text-primary-600' : 'text-neutral-100'
															}`}
														>
															Pago inmediato
														</h4>
														{!isFinanced && (
															<div className="rounded-full bg-primary-500/20 px-2 py-0.5">
																<span className="text-xs font-medium text-primary-400">Seleccionado</span>
															</div>
														)}
													</div>
													<p className="mt-1 text-xs leading-relaxed text-neutral-400">
														El cliente pagará por su recarga el día de hoy en la tienda
													</p>
												</div>
											</div>
										</div>

										{/* Financed Payment Option - for Venezuela always show (disabled if not available), for other countries only show if available */}
										{(country === 'venezuela' || canUserFinance) && (
											<div
												onClick={() => {
													if (canUserFinance) {
														setIsFinanced(true);
													}
												}}
												className={`relative rounded-xl border-2 p-4 transition-all duration-200 ${
													canUserFinance
														? `cursor-pointer hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] ${
																isFinanced
																	? 'border-primary-500 bg-primary-500/5 shadow-md shadow-primary-500/20 ring-1 ring-primary-500/20'
																	: 'border-neutral-700 bg-neutral-900/50 hover:border-neutral-600 hover:bg-neutral-900/70'
															}`
														: 'cursor-not-allowed border-neutral-800 bg-neutral-900/30 opacity-50'
												}`}
											>
												<div className="flex items-start space-x-3">
													<div
														className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200 ${
															canUserFinance && isFinanced
																? 'border-primary-500 bg-primary-500 shadow-sm shadow-primary-500/30'
																: 'border-neutral-600 bg-neutral-900'
														}`}
													>
														{canUserFinance && isFinanced && (
															<div className="h-2 w-2 rounded-full bg-white duration-200 animate-in zoom-in-50"></div>
														)}
													</div>
													<div className="min-w-0 flex-1">
														<div className="flex items-center justify-between">
															<h4
																className={`text-sm font-medium transition-colors ${
																	canUserFinance && isFinanced ? 'text-primary-400 light:text-primary-600' : 'text-neutral-100'
																}`}
															>
																Pago financiado
															</h4>
															{canUserFinance && isFinanced && (
																<div className="rounded-full bg-primary-500/20 px-2 py-0.5">
																	<span className="text-xs font-medium text-primary-400">Seleccionado</span>
																</div>
															)}
														</div>
														<p className="mt-1 text-xs leading-relaxed text-neutral-400">
															{canUserFinance
																? 'El cliente pagará su recarga en 15 días directamente a Krece'
																: 'Se requiere una cuenta Krece para financiar'}
														</p>
													</div>
												</div>
											</div>
										)}
									</div>
								</div>

								<div className="rounded-lg border border-neutral-800 bg-neutral-925 p-4">
									{userIdentificationNumber && (
										<div className="flex items-center justify-between py-1 text-sm text-neutral-300">
											<span>Cliente</span>
											<span className="font-medium text-neutral-100">{userIdentificationNumber}</span>
										</div>
									)}
									<div className="flex items-center justify-between py-1 text-sm text-neutral-300">
										<span>Teléfono</span>
										<span className="font-medium text-neutral-100">{phoneNumber}</span>
									</div>
									<div className="flex items-center justify-between py-1 text-sm text-neutral-300">
										<span>Operador</span>
										<span className="font-medium text-neutral-100">{operator ? ucfirst(operator) : '—'}</span>
									</div>
									<div className="flex items-center justify-between py-1 text-sm text-neutral-300">
										<span>Monto</span>
										<span className="font-medium text-neutral-100">
											{country === 'venezuela' ? 'Bs. ' : currencyCode} {format_currency(amount || 0, true)} {country === 'venezuela' && `($${format_currency((amount || 0) / bcvRate, false)})`}
										</span>
									</div>
									{effectiveFee > 0 && (
										<div className="flex items-center justify-between py-1 text-sm text-neutral-300">
											<span>Fee</span>
											<span className="font-medium text-neutral-100">
												{currencyCode} {format_currency(effectiveFee)}
												{country === 'venezuela' ? <span className="text-xs text-neutral-400"> (incl. IVA)</span> : ''}
											</span>
										</div>
									)}
									<div className="mt-2 border-t border-neutral-800/60 pt-2">
										<div className="flex items-center justify-between py-1 text-sm text-neutral-200">
											<span>{!isFinanced ? 'Pago inmediato en tienda:' : 'Cliente pagará en 15 días:'}</span>
											<span className="font-semibold">
												{currencyCode}{format_currency(isFinanced ? financedTotal : amount || 0)}
											</span>
										</div>
									</div>
								</div>

								{userData?.master_user && sub_stores.length > 0 && (
									<Select
										floatingLabel="Local de venta"
										options={sub_stores.map((store) => ({
											label: store,
											value: store
										}))}
										defaultValue={subStore}
										placeholder="Selecciona un local"
										onSelectedChange={(val) => {
											setSubStore(typeof val === 'string' ? val : '');
										}}
										containerClassName="!mt-6"
									/>
								)}

								{error && <Notice message={error} variant="error" />}

								{/* Payment Confirmation Checkbox */}
								<div
									className={`flex cursor-pointer select-none items-center gap-3 rounded p-2 transition-colors hover:bg-neutral-800/30 ${
										checkboxChecked ? 'border border-blue-600/40 bg-neutral-800/40' : ''
									}`}
									onClick={() => setCheckboxChecked(!checkboxChecked)}
								>
									<div
										className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${
											checkboxChecked
												? 'border-blue-600 bg-blue-600 shadow-sm shadow-blue-600/30'
												: 'border-neutral-600 bg-neutral-900'
										}`}
									>
										{checkboxChecked && <div className="h-2 w-2 rounded-full bg-white duration-200 animate-in zoom-in-50"></div>}
									</div>
									<div className="flex-1">
										<p className="text-sm leading-relaxed text-neutral-200">
											{!isFinanced
												? 'Confirmo que el cliente ha pagado por su recarga en la tienda.'
												: 'Confirmo que he informado al cliente que deberá pagar su recarga en máximo 15 días directamente a Krece.'}
										</p>
									</div>
								</div>

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										loading={loading}
										disabled={loading || !checkboxChecked || (userData?.master_user && (!subStore || sub_stores.length === 0))}
										onClick={async () => {
											setLoading(true);
											setError('');

											if (isFinanced) {
												// Generate contract for financed payments
												try {
													// Refresh before committing, even if the tab slept through the expiry timer.
													const [conditions, conditionsError] = await callAPI(
														'https://api.krece.app/users/financing-overrides/',
														'GET',
														{
															user_email: userEmail,
															product_type: 'recarga',
															amount_financed: financingPrincipal,
															installments: 1
														},
														userData,
														country
													);
													if (conditionsError || !conditions) {
														setError(
															conditionsError || 'No se pudieron consultar las condiciones del cliente. Intente nuevamente.'
														);
														return;
													}
													const expiry = conditions.financing_overrides.expires_at
														? Date.parse(conditions.financing_overrides.expires_at)
														: null;
													const currentFee =
														expiry !== null && expiry <= Date.now()
															? undefined
															: conditions.active_financing_overrides.recarga?.fee;
													setRecargaFeeOverride(currentFee);
													setRecargaFeeOverrideExpiresAt(currentFee !== undefined && Number.isFinite(expiry) ? expiry : null);
													if (currentFee !== recargaFeeOverride) {
														setCheckboxChecked(false);
														setError('Las condiciones cambiaron. Revisa el total y confirma nuevamente.');
														return;
													}
													const quote = currentFee !== undefined ? conditions.quote : null;
													if (currentFee !== undefined && !quote) {
														setError('No se pudieron calcular las condiciones del cliente. Intente nuevamente.');
														return;
													}
													const [response, error] = await callAPI(
														'https://api.krece.app/registration/anexoregistration/' as const,
														'POST',
														{
															seller_store: seller_store || '',
															store_user: store_user || '',
															sub_store: (!userData?.master_user ? userData?.store_name : subStore) || '',
															operator,
															phone_number: phoneNumber,
															device_imei: '',
															loan_duration: 0.5,
															loan_payment_cycle: 15,
															device_type: 'recarga',
															// Override contracts use the backend quote in ledger currency (USD in Venezuela).
															// The provider request in handleFinalizeRecharge still sends the original local amount.
															payment_amount:
																quote?.payment_amount ??
																((amount || 0) + (country === 'venezuela' ? effectiveFee * bcvRate : effectiveFee)),
															amount_financed: quote?.amount_financed ?? (amount || 0),
															device_price: quote?.amount_financed ?? (amount || 0),
															country,
															user_document_id: userEmail,
															self_financed: !isFinanced
														},
														userData,
														country
													);

													if (error || !response?.success) {
														setError(error || response?.status || 'Ocurrió un problema, por favor intente nuevamente.');
														return;
													}

													setPage(5);
												} catch (error) {
													setError('Ocurrió un problema, por favor intente nuevamente.');
												} finally {
													setLoading(false);
												}
											} else {
												// Skip contract for immediate payments - finalize directly
												await handleFinalizeRecharge();
											}
										}}
										variant="primary"
									>
										{isFinanced ? 'Generar contrato' : 'Confirmar recarga'}
									</Button>
									<Button Icon={<ArrowLeft className="size-5" />} type="button" onClick={() => setPage(3)} variant="secondary">
										Atrás
									</Button>
								</div>
							</div>
						)}

					{page === 5 &&
						isFinanced &&
						render_header_and_content(
							contractAccepted ? 'Procesando recarga' : 'Esperando aprobación',
							contractAccepted
								? 'Finalizando la recarga...'
								: 'El contrato se ha generado. Una vez aprobado, la recarga se procesará automáticamente.',
							null,
							<div className="space-y-6 text-center">
								{contractAccepted ? (
									<>
										{!error ? (
											<>
												<div className="relative mx-auto flex h-20 w-20 items-center justify-center">
													{/* Outer breathing ring with smooth scale animation */}
													<div className="absolute inset-0 animate-[ping_2s_cubic-bezier(0.4,0,0.6,1)_infinite] rounded-full bg-gradient-to-br from-green-500/30 to-green-600/30 opacity-30"></div>
													{/* Inner breathing circle */}
													<div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-500/20 to-green-600/20 shadow-lg shadow-green-500/10">
														<div className="flex h-12 w-12 animate-[pulse_3s_ease-in-out_infinite] items-center justify-center rounded-full bg-green-500/15">
															<CircleCheck className="h-6 w-6 text-green-400" />
														</div>
													</div>
												</div>
												<div className="space-y-2">
													<h2 className="text-xl font-semibold tracking-tight text-neutral-0">¡Contrato aprobado!</h2>
													<p className="text-sm text-neutral-300">Procesando la recarga, por favor espere...</p>
												</div>
											</>
										) : (
											<>
												<div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-red-500/20 to-red-600/20 shadow-lg shadow-red-500/10">
													<div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15">
														<AlertTriangle className="h-8 w-8 text-red-400" />
													</div>
												</div>
												<div className="space-y-2">
													<h2 className="text-xl font-semibold tracking-tight text-neutral-0">Error al procesar</h2>
													<p className="text-sm text-neutral-300">Ocurrió un problema al finalizar la recarga.</p>
												</div>
												<Notice message={error} variant="error" />
												<div className="mx-auto flex w-full max-w-xs flex-col gap-3">
													<Button
														onClick={() => {
															setError('');
															handleFinalizeRecharge();
														}}
														variant="primary"
														loading={loading}
														className="w-full"
													>
														Reintentar
													</Button>
													<Button
														Icon={<ArrowLeft className="size-4" />}
														onClick={() => {
															setPage(4);
															setContractAccepted(false);
															setError('');
														}}
														variant="secondary"
														className="w-full"
													>
														Atrás
													</Button>
												</div>
											</>
										)}
									</>
								) : (
									<>
										<div className="relative mx-auto flex h-20 w-20 items-center justify-center">
											{/* Outer breathing ring with smooth scale animation */}
											<div className="absolute inset-0 animate-[ping_2s_cubic-bezier(0.4,0,0.6,1)_infinite] rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/30 opacity-30"></div>
											{/* Inner breathing circle */}
											<div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/20 shadow-lg shadow-blue-500/10">
												<div className="flex h-12 w-12 animate-[pulse_3s_ease-in-out_infinite] items-center justify-center rounded-full bg-blue-500/15">
													<Clock className="h-6 w-6 text-blue-400" />
												</div>
											</div>
										</div>
										<div className="space-y-2">
											<h2 className="text-xl font-semibold tracking-tight text-neutral-0">
												El contrato está generado y esperando aprobación
											</h2>
											<p className="text-sm text-neutral-300">
												Indicar al cliente que debe ir a su aplicación de Krece y aprobar su contrato.
											</p>
										</div>
										<div className="pt-4">
											<Button
												Icon={<ArrowLeft className="size-4" />}
												onClick={() => setPage(4)}
												variant="secondary"
												className="w-full max-w-xs"
											>
												Atrás
											</Button>
										</div>
									</>
								)}
							</div>
						)}

					{page === 6 && !userIdentificationNumber && (
						<div className="space-y-6 text-center">
							<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/15">
								<CircleCheck className="h-8 w-8 text-green-400" />
							</div>
							<h2 className="text-2xl font-semibold tracking-tight text-neutral-0">¡Recarga exitosa!</h2>
							<p className="text-neutral-300">La recarga se está procesando. Llegará en unos minutos.</p>

							{/* Offer registration with coupon for new users */}
							<div className="relative overflow-hidden rounded-2xl border border-neutral-700/30 bg-neutral-950/20 backdrop-blur-md p-6 shadow-2xl ring-1 ring-neutral-700/20">
								{/* Subtle gradient overlay */}
								<div className="absolute inset-0 bg-gradient-to-br from-primary-500/8 via-transparent to-primary-600/5" />

								<div className="relative z-10">
									{/* Icon container with enhanced styling */}
									<div className="mb-6 flex justify-center">
										<div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500/15 backdrop-blur-sm ring-1 ring-primary-500/25 shadow-lg">
											<TicketPercent className="h-7 w-7 text-primary-400" />
										</div>
									</div>

									{/* Title with better typography */}
									<h3 className="mb-3 text-center text-xl font-semibold text-neutral-50 tracking-tight">
										¿Registrar al cliente?
									</h3>

									{/* Description with improved spacing and readability */}
									<p className="mb-6 text-center text-sm leading-relaxed text-neutral-200 max-w-md mx-auto">
										Si procedes con el registro del usuario ahora, el cliente recibirá un cupón que podrá usar en su primera compra
										con Krece.
									</p>

									{/* Action buttons with improved layout */}
									<div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
										<Button
											onClick={() => {
												setShowRegistration(true);
											}}
											variant="primary"
											className="w-full sm:w-auto px-6 py-2.5 font-medium shadow-lg hover:shadow-primary-500/30 transition-all duration-200 hover:scale-[1.02]"
										>
											Registrar cliente
										</Button>
										<Button
											onClick={() => {
												window.location.reload();
											}}
											variant="secondary"
											className="w-full sm:w-auto px-6 py-2.5 font-medium hover:bg-neutral-700/40 transition-all duration-200 hover:scale-[1.02]"
										>
											Finalizar
										</Button>
									</div>
								</div>
							</div>
						</div>
					)}

					{page === 6 && userIdentificationNumber && (
						<div className="space-y-6 text-center">
							<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/15">
								<CircleCheck className="h-8 w-8 text-green-400" />
							</div>
							<h2 className="text-2xl font-semibold tracking-tight text-neutral-0">¡Recarga exitosa!</h2>
							<p className="text-neutral-300">
								{isFinanced
									? 'El contrato ha sido aprobado y la recarga se está procesando. Llegará en unos minutos.'
									: 'La recarga se está procesando. Llegará en unos minutos.'}
							</p>
							<Button
								onClick={() => {
									window.location.reload();
								}}
								variant="primary"
							>
								Nueva recarga
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);

	function render_header_and_content(
		title: string,
		description: string | null,
		icon: React.ReactElement | null,
		content: React.ReactNode
	) {
		return (
			<>
				<div className="mb-4 flex flex-col items-center gap-2 md:mb-6 md:gap-3">
					{icon && (
						<div className="relative">
							<div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
							<div className="relative rounded-full bg-neutral-900 p-4 light:bg-neutral-1000 light:ring-2 light:ring-primary-500/10">
								{React.cloneElement(icon, { className: 'h-6 w-6 text-primary-500' })}
							</div>
						</div>
					)}
					<div className="space-y-1 text-center">
						<h1 className="text-2xl font-semibold tracking-tighter text-neutral-0 md:text-3xl">{title}</h1>
						{description && <p className="text-neutral-300">{description}</p>}
					</div>
				</div>
				{content}
			</>
		);
	}
}

function useRecargaAvailability(CountrySettings: CountrySettingsType, userData: UserDataType, country: string) {
	const [checkingAvailability, setCheckingAvailability] = useState(true);
	const [serviceAvailable, setServiceAvailable] = useState(false);
	const [availabilityMessage, setAvailabilityMessage] = useState('');

	useEffect(() => {
		(async () => {
			try {
				setCheckingAvailability(true);

				// First check if recargas service exists in CountrySettings
				if (!CountrySettings?.services_config?.recargas?.is_active) {
					setServiceAvailable(false);
					setAvailabilityMessage('El servicio de recargas no está disponible en este momento.');
					return;
				}

				const [response, error] = await callAPI(
					'https://api.krece.app/registration/recargaavailable/',
					'GET',
					undefined,
					userData,
					country
				);

				if (!error && response?.balance_available && response?.recarga_sales_access) {
					setServiceAvailable(true);
				} else {
					if (response?.balance_available && !response?.recarga_sales_access) {
						setAvailabilityMessage('El servicio de recargas no está habilitado para esta cuenta.');
					} else {
						setAvailabilityMessage(error || response?.status || 'El servicio de recargas no está disponible en este momento.');
					}
				}
			} catch (_) {
				setServiceAvailable(false);
				setAvailabilityMessage('El servicio de recargas no está disponible en este momento.');
			} finally {
				setCheckingAvailability(false);
			}
		})();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [CountrySettings?.services_config?.recargas?.is_active, userData, country]);

	return { checkingAvailability, serviceAvailable, availabilityMessage };
}

function getAmountOptions(
	CountrySettings: CountrySettingsType,
	country: string,
	operator: string,
	isNewUser: boolean,
	bcvRate: number
): number[] {
	// Use operator-specific min/max/step from CountrySettings if available
	const operatorConfig = CountrySettings?.services_config?.recargas?.operators?.[operator as "viva"];
	if (
		operatorConfig &&
		operatorConfig.min !== undefined &&
		operatorConfig.max !== undefined &&
		operatorConfig.step !== undefined
	) {
		let { min, max, step } = operatorConfig;

		// If user is new and new_user_max is defined, use it as the max
		if (isNewUser && operatorConfig?.new_user_max !== undefined) {
			let newUserMax = operatorConfig.new_user_max;

			// For Venezuela, convert new_user_max from dollars to Bs using BCV rate
			if (country === 'venezuela') {
				newUserMax = newUserMax * bcvRate;
			}

			// Use the new_user_max as the max, but ensure it's not less than min
			max = Math.max(min, newUserMax);
		}

		const options: number[] = [];
		for (let amount = min; amount <= max; amount += step) {
			options.push(amount);
		}
		return options;
	}

	// No fallback - return empty array if options are not available
	return [];
}
